import type {
  AvailableLoyaltyPointsResponse,
  LoyaltyPointGrant,
  LoyaltyPointGrantCreate,
  LoyaltyPointGrantUpdate,
  LoyaltyPointMutationResponse,
  LoyaltyPointTransaction,
} from "@/types/loyalty-points";
import type { Page } from "@/types/pagination";

import { requestData } from "@/api/api-config";

export interface LoyaltyPointGrantListParams {
  page?: number;
  perPage?: number;
  includeExpired?: boolean;
  includeUsed?: boolean;
}

export async function getPatientLoyaltyPoints(
  patientId: string,
  params?: LoyaltyPointGrantListParams,
): Promise<Page<LoyaltyPointGrant> | null> {
  return await requestData<Page<LoyaltyPointGrant>>(
    `user/patient/${patientId}/loyalty-points`,
    "get",
    {
      searchParams: {
        page: params?.page,
        per_page: params?.perPage,
        include_expired: params?.includeExpired,
        include_used: params?.includeUsed,
      },
    },
  );
}

export async function getAvailableLoyaltyPoints(
  patientId: string,
): Promise<AvailableLoyaltyPointsResponse | null> {
  return await requestData<AvailableLoyaltyPointsResponse>(
    `user/patient/${patientId}/loyalty-points/available`,
    "get",
  );
}

// Mutations pass catchError=false so HTTP failures throw instead of resolving
// null — otherwise React Query treats a rejected write as success (form closes,
// success toast) even though nothing changed.
export async function createLoyaltyPointGrant(
  patientId: string,
  data: LoyaltyPointGrantCreate,
): Promise<LoyaltyPointMutationResponse> {
  const { patientId: _, ...bodyData } = data;
  return (await requestData<LoyaltyPointMutationResponse>(
    `user/patient/${patientId}/loyalty-points`,
    "post",
    {
      json: bodyData,
    },
    false,
  ))!;
}

export async function updateLoyaltyPointGrant(
  patientId: string,
  grantId: string,
  data: LoyaltyPointGrantUpdate,
): Promise<LoyaltyPointMutationResponse> {
  return (await requestData<LoyaltyPointMutationResponse>(
    `user/patient/${patientId}/loyalty-points/${grantId}`,
    "patch",
    {
      json: data,
    },
    false,
  ))!;
}

export async function deleteLoyaltyPointGrant(
  patientId: string,
  grantId: string,
): Promise<LoyaltyPointMutationResponse> {
  return (await requestData<LoyaltyPointMutationResponse>(
    `user/patient/${patientId}/loyalty-points/${grantId}`,
    "delete",
    undefined,
    false,
  ))!;
}

export async function getLoyaltyPointsLog(
  patientId: string,
  grantId?: string,
): Promise<LoyaltyPointTransaction[] | null> {
  return await requestData<LoyaltyPointTransaction[]>(
    `user/patient/${patientId}/loyalty-points/log`,
    "get",
    {
      searchParams: {
        grant_id: grantId,
      },
    },
  );
}
